import { Swiper } from 'swiper';
import { A11y, Navigation, Pagination } from 'swiper/modules';
import { __ } from '@wordpress/i18n';

const blocks = document.querySelectorAll('.block-content-tour');

blocks.forEach((block) => {
	const slider = block.querySelector('.block-content-tour .swiper');
	const imageWrapper = block.querySelector(
		'.block-content-tour__image-wrapper'
	);
	const cards = block.querySelectorAll('.block-content-tour-card');
	let highResImage;
	let currentCardImage = null;
	let phaseElements = [];
	let isNearViewport = false;
	const focusButtons = {};
	const cardMarkers = {};
	const cardOffsets = {};

	if (!slider || !imageWrapper || 0 === cards.length) {
		return;
	}

	// Sibling of the image wrapper, so its contents escape the zoom transform.
	const isMapTour = block.classList.contains('block-content-tour--map');
	const figure = block.querySelector('.block-content-tour__figure');
	const grid = block.querySelector('.block-content-tour__grid');
	const content = block.querySelector('.block-content-tour__content');
	const unzoomedLayer = document.createElement('div');
	unzoomedLayer.className = 'block-content-tour__unzoomed';

	if (isMapTour && figure) {
		phaseElements = block.querySelectorAll('.block-content-tour__phase');
		figure.appendChild(unzoomedLayer);
	}

	const customPhasesElement = block.querySelector(
		'.block-content-tour__phases--custom .block-content-tour__phases-inner'
	);

	// Must stay in step with the transition on .block-content-tour__card-image.
	const cardImageFadeDuration = 700;

	/**
	 * Point an image at a slide's sources.
	 *
	 * Shared with the preloader so both request the same cached candidate.
	 *
	 * @param {HTMLImageElement} image The image to apply the sources to.
	 * @param {HTMLElement}      slide The slide to take the sources from.
	 */
	const applyImageSources = (image, slide) => {
		// Before srcset, or the browser resolves candidates against 100vw.
		if (slide?.dataset?.imageSizes) {
			image.sizes = slide.dataset.imageSizes;
		}

		if (slide?.dataset?.imageSrcset) {
			image.srcset = slide.dataset.imageSrcset;
		}

		image.src = slide?.dataset?.imageSrc ?? '';
	};

	/**
	 * Fade out and discard every card image except the current one.
	 */
	const removeStaleCardImages = () => {
		const staleImages = unzoomedLayer.querySelectorAll(
			'.block-content-tour__card-image'
		);

		staleImages.forEach((staleImage) => {
			if (staleImage === currentCardImage) {
				return;
			}

			staleImage.classList.remove(
				'block-content-tour__card-image--visible'
			);

			setTimeout(() => staleImage.remove(), cardImageFadeDuration);
		});
	};

	/**
	 * Cross-fade the figure to the image belonging to the given slide.
	 *
	 * Fades in only once loaded, so the outgoing image is never replaced by a blank.
	 *
	 * @param {HTMLElement} slide       The slide to take the image from.
	 * @param {number}      activeIndex Index of the card being viewed.
	 */
	const setCardImage = (slide, activeIndex) => {
		const imageSrc = slide?.dataset?.imageSrc ?? '';

		if (currentCardImage?.dataset?.src === imageSrc) {
			return;
		}

		// No image on this card, so fall back to the parent block image.
		if (!imageSrc) {
			currentCardImage = null;
			block.classList.remove('block-content-tour--card-image-active');
			removeStaleCardImages();
			return;
		}

		const cardImage = document.createElement('img');
		cardImage.className = 'block-content-tour__card-image';
		cardImage.dataset.src = imageSrc;
		cardImage.dataset.cardIndex = activeIndex;
		cardImage.alt = slide?.dataset?.imageAlt ?? '';
		cardImage.style.setProperty(
			'--cardImageOffset',
			cardOffsets[activeIndex] ?? '0px'
		);

		applyImageSources(cardImage, slide);

		const reveal = () => {
			if (cardImage !== currentCardImage) {
				cardImage.remove();
				return;
			}

			// Wait a frame so the browser registers the starting opacity.
			requestAnimationFrame(() => {
				cardImage.classList.add(
					'block-content-tour__card-image--visible'
				);
				block.classList.add('block-content-tour--card-image-active');
				removeStaleCardImages();
			});
		};

		cardImage.addEventListener('load', reveal, { once: true });

		unzoomedLayer.appendChild(cardImage);
		currentCardImage = cardImage;

		if (cardImage.complete) {
			reveal();
		}
	};

	/**
	 * Work out how far each card must slide to centre its own marker.
	 *
	 * Held per card rather than on the layer, so a card keeps its position
	 * while it fades and never shifts with the one replacing it.
	 */
	const measureCardOffsets = () => {
		if (!figure) {
			return;
		}

		const figureRect = figure.getBoundingClientRect();
		const gridRect = (grid ?? figure).getBoundingClientRect();
		const gridLeft = gridRect.left - figureRect.left;
		const gridRight = gridRect.right - figureRect.left;

		// A card may slide only until one of its own edges reaches the grid's.
		const maxOffset = gridLeft;
		const minOffset = gridRight - unzoomedLayer.offsetWidth;

		// The slide content covers the left of the figure, so markers centre in
		// the area beside it rather than across the figure as a whole.
		const areaStart = content
			? content.getBoundingClientRect().right - figureRect.left
			: gridLeft;
		const areaCentre = (areaStart + gridRight) / 2;

		cards.forEach((card, index) => {
			const marker = cardMarkers[index];
			// Nothing to slide, so centre it the way the main image is centred.
			let offset = (maxOffset + minOffset) / 2;

			if (marker && minOffset < maxOffset) {
				const markerCentre = marker.offsetLeft + marker.offsetWidth / 2;

				offset = Math.min(
					maxOffset,
					Math.max(minOffset, areaCentre - markerCentre)
				);
			}

			cardOffsets[index] = `${offset}px`;
			marker?.style.setProperty('--cardImageOffset', cardOffsets[index]);
		});

		const mountedImages = unzoomedLayer.querySelectorAll(
			'.block-content-tour__card-image'
		);

		mountedImages.forEach((image) => {
			image.style.setProperty(
				'--cardImageOffset',
				cardOffsets[image.dataset.cardIndex] ?? '0px'
			);
		});
	};

	/**
	 * Flag that an individual card is showing rather than the overview.
	 *
	 * The map version has no zoom to derive this from, but the phases panel and
	 * figure gradients still key off it.
	 *
	 * @param {HTMLElement} slide The slide being shown.
	 */
	const setFocusedState = (slide) => {
		const width = parseFloat(slide?.dataset?.width ?? '100%');

		block.classList.toggle(
			'block-content-tour--focused',
			100 !== width || Boolean(slide?.dataset?.imageSrc)
		);
	};

	/**
	 * Zoom the figure into the slide's focus area on the main image.
	 *
	 * @param {HTMLElement} slide The slide to take the coordinates from.
	 */
	const applyImageTransform = (slide) => {
		const cardX = parseFloat(slide?.dataset?.x ?? '0%');
		const cardY = parseFloat(slide?.dataset?.y ?? '0%');
		let cardWidth = parseFloat(slide?.dataset?.width ?? '100%');
		let cardHeight = parseFloat(slide?.dataset?.height ?? '100%');

		let cardXCenter = cardX + cardWidth / 2;
		let cardYCenter = cardY + cardHeight / 2;

		if (0 === cardWidth) {
			cardWidth = 30;
			cardXCenter = cardX;
		}

		if (0 === cardHeight) {
			cardHeight = 30;
			cardYCenter = cardY;
		}

		let transformXScale = 100 / cardWidth;
		let transformYScale = 100 / cardHeight;

		if (transformXScale > transformYScale) {
			transformXScale = transformYScale;
		} else {
			transformYScale = transformXScale;
		}

		const maxCardXCenter = 100 - 50 / transformXScale;

		block.classList.remove('block-content-tour--offset-left');
		block.classList.remove('block-content-tour--offset-right');

		if (cardXCenter > maxCardXCenter) {
			cardXCenter = maxCardXCenter;
			block.classList.add('block-content-tour--offset-right');
		}

		const minCardXCenter = 50 / transformXScale;

		if (cardXCenter < minCardXCenter) {
			cardXCenter = minCardXCenter;
			block.classList.add('block-content-tour--offset-left');
		}

		const maxCardYCenter = 100 - 50 / transformYScale;

		if (cardYCenter > maxCardYCenter) {
			cardYCenter = maxCardYCenter;
		}

		const minCardYCenter = 50 / transformYScale;

		if (cardYCenter < minCardYCenter) {
			cardYCenter = minCardYCenter;
		}

		const transformXCenter = 50 - cardXCenter;
		const transformYCenter = 50 - cardYCenter;

		if (1 === transformYScale && 1 === transformXScale) {
			block.classList.remove('block-content-tour--focused');
			block.style.removeProperty('--card-x-center');
			block.style.removeProperty('--card-y-center');
			block.style.setProperty('--transform-x-center', '0%');
			block.style.setProperty('--transform-y-center', '0%');
			block.style.removeProperty('--transform-x-scale');
			block.style.removeProperty('--transform-y-scale');
		} else {
			block.classList.add('block-content-tour--focused');

			block.style.setProperty('--card-x-center', `${cardXCenter}%`);
			block.style.setProperty('--card-y-center', `${cardYCenter}%`);
			block.style.setProperty(
				'--transform-x-center',
				`${transformXCenter}%`
			);
			block.style.setProperty(
				'--transform-y-center',
				`${transformYCenter}%`
			);
			block.style.setProperty(
				'--transform-x-scale',
				`${transformXScale}`
			);
			block.style.setProperty(
				'--transform-y-scale',
				`${transformYScale}`
			);
		}
	};

	/**
	 * Bring the figure, markers and phases in line with the slider's current slide.
	 *
	 * @param {Swiper} swiper The card slider.
	 */
	const showSlide = (swiper) => {
		const currentSlide = swiper.slides[swiper.activeIndex];

		if (!currentSlide) {
			return;
		}

		// The map version never zooms; it cross-fades between card images.
		if (!isMapTour) {
			applyImageTransform(currentSlide);
		}

		if (!highResImage && block?.dataset?.highresImage) {
			highResImage = document.createElement('img');
			highResImage.className = 'block-content-tour__highres-image';
			highResImage.src = block.dataset.highresImage;
			highResImage.alt = block.dataset.imageAlt || '';
			imageWrapper.appendChild(highResImage);

			highResImage.onload = () => {
				block.classList.add('block-content-tour--highres-loaded');
			};

			if (highResImage.complete) {
				block.classList.add('block-content-tour--highres-loaded');
			}
		}

		if (isMapTour) {
			setFocusedState(currentSlide);

			// Until then, the image observer below takes care of it.
			if (isNearViewport) {
				setCardImage(currentSlide, swiper.realIndex);
			}
		}

		if (isMapTour && phaseElements.length > 0) {
			const phases = (currentSlide?.dataset?.phases ?? '').split(',');
			const customPhases = (
				currentSlide?.dataset?.customPhases ?? ''
			).split(',');

			if (currentSlide?.dataset?.customPhases && customPhasesElement) {
				customPhasesElement.innerHTML = '';

				customPhases.forEach((phase, index) => {
					const phaseState = phases[index] || '1';
					const phaseDiv = document.createElement('div');
					phaseDiv.className = `block-content-tour__phase block-content-tour__phase--${phaseState}`;
					phaseDiv.innerText = phase;
					const phaseIconSpan = document.createElement('span');
					phaseIconSpan.className = 'icon';
					phaseDiv.insertBefore(phaseIconSpan, phaseDiv.firstChild);
					customPhasesElement.appendChild(phaseDiv);
					customPhasesElement.parentElement.classList.add(
						'block-content-tour__phases--active'
					);
				});
			} else {
				if (customPhasesElement) {
					customPhasesElement.innerHTML = '';
					customPhasesElement.parentElement.classList.remove(
						'block-content-tour__phases--active'
					);
				}

				phaseElements.forEach((phaseElement, index) => {
					const phaseState = phases[index] || '1';

					phaseElement.classList.remove(
						'block-content-tour__phase--1',
						'block-content-tour__phase--2',
						'block-content-tour__phase--3'
					);

					phaseElement.classList.add(
						`block-content-tour__phase--${phaseState}`
					);
				});
			}
		}

		[focusButtons, cardMarkers].forEach((markers) => {
			Object.values(markers).forEach((marker) => {
				marker.classList.remove(
					'block-content-tour__focus-button--active'
				);
			});

			markers[swiper.realIndex]?.classList?.add(
				'block-content-tour__focus-button--active'
			);
		});
	};

	const settings = {
		// Swiper can't loop a single slide, and warns when asked to.
		loop: cards.length > 1,
		grabCursor: true,
		spaceBetween: 10,
		slidesPerView: 1,
		speed: 600,
		autoHeight: true,
		modules: [A11y, Navigation, Pagination],
		slideClass: 'block-content-tour-card',
		a11y: {
			itemRoleDescriptionMessage: 'slide',
		},
		pagination: {
			el: '.swiper-pagination',
			type: 'fraction',
			formatFractionCurrent: (number) => String(number).padStart(2, '0'),
			formatFractionTotal: (number) => String(number).padStart(2, '0'),
			renderFraction: (currentClass, totalClass) => {
				return `<span class="${currentClass}"></span>/<span class="${totalClass}"></span>`;
			},
		},
		navigation: {
			prevEl: '.swiper-button-prev',
			nextEl: '.swiper-button-next',
		},
		breakpoints: {
			448: {
				autoHeight: true,
			},
			768: {
				autoHeight: false,
			},
		},
		on: {
			transitionStart: showSlide,
		},
	};

	const cardSwiper = new Swiper(slider, settings);

	if (isMapTour) {
		// Held back until the block nears the viewport, to keep the images
		// off the initial page load. Warms the cache for the rest of the tour.
		const imageObserver = new IntersectionObserver(
			(entries, observer) => {
				entries.forEach((entry) => {
					if (!entry.isIntersecting) {
						return;
					}

					observer.unobserve(entry.target);
					isNearViewport = true;

					setCardImage(
						cardSwiper.slides[cardSwiper.activeIndex],
						cardSwiper.realIndex
					);

					cards.forEach((card) => {
						if (!card?.dataset?.imageSrc) {
							return;
						}

						const preloadImage = new Image();
						preloadImage.fetchPriority = 'low';
						preloadImage.decoding = 'async';
						applyImageSources(preloadImage, card);
					});
				});
			},
			{
				root: null,
				rootMargin: '200px 0px 200px 0px',
				threshold: [0],
			}
		);

		imageObserver.observe(block);

		if (figure) {
			const layoutObserver = new ResizeObserver(measureCardOffsets);

			layoutObserver.observe(figure);
		}
	}

	cards.forEach((card, index) => {
		// Its own element rather than a moved one, so the two can cross-fade.
		if (isMapTour && card?.dataset?.cardX) {
			const cardMarker = document.createElement('div');
			cardMarker.className =
				'block-content-tour__focus-button block-content-tour__focus-button--card';
			cardMarker.style.setProperty('--x', card.dataset.cardX);
			cardMarker.style.setProperty('--y', card.dataset.cardY);
			cardMarker.style.setProperty('--width', card.dataset.cardWidth);
			cardMarker.style.setProperty('--height', card.dataset.cardHeight);
			cardMarker.setAttribute('aria-hidden', 'true');
			cardMarkers[index] = cardMarker;
			unzoomedLayer.appendChild(cardMarker);
		}

		const cardX = card?.dataset?.x ?? null;
		const cardY = card?.dataset?.y ?? null;
		const cardWidth = card?.dataset?.width ?? null;
		const cardHeight = card?.dataset?.height ?? null;

		if (!cardX || !cardY || !cardWidth || !cardHeight) {
			return;
		}

		const cardHeading = card.querySelector('.wp-block-heading');
		const button = document.createElement('button');
		button.className = 'block-content-tour__focus-button';
		button.type = 'button';
		button.innerText = cardHeading.innerText || __('View slide', 'propel');
		button.style.setProperty('--x', cardX);
		button.style.setProperty('--y', cardY);
		button.style.setProperty('--width', cardWidth);
		button.style.setProperty('--height', cardHeight);
		button.setAttribute('data-index', index);
		focusButtons[index] = button;

		imageWrapper.appendChild(button);

		button.addEventListener('click', () => {
			cardSwiper.slideToLoop(index);
		});
	});

	// Swiper never fires transitionStart for a lone card, and when it does on
	// init, the markers above don't exist yet.
	if (isMapTour) {
		showSlide(cardSwiper);
	}

	const thumbnail = block.querySelector('.block-content-tour__thumbnail');

	if (thumbnail) {
		thumbnail.addEventListener('click', () => {
			cardSwiper.slideToLoop(0);
		});
	}
});
